/**
 * Sprint BI Part 2 — SVG sanitize + dataURL 化 helper。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/svgUtils.ts` を L2 で移植 (logic 完全同等)。
 * Decoration の `svgSource` を Firestore に保存する前と `<img>` 表示前に必ず通す。
 *
 * 動作:
 *   - allowed tags のみ残し、script / foreignObject / iframe / a / image / use / animate*
 *     等は除去
 *   - on* イベント属性、外部 URL を含む href / xlink:href、style 中の url() / expression を除去
 *   - parser error 時は空文字を返す (caller 側でエラー扱い)
 *
 * DOM Parser を使うため、test (jsdom) でも動作する。
 */

const ALLOWED_TAGS = new Set([
  'svg',
  'g',
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polygon',
  'polyline',
  'defs',
  'lineargradient',
  'radialgradient',
  'stop',
  'title',
  'desc',
]);

const FORBIDDEN_TAGS = new Set([
  'script',
  'foreignobject',
  'iframe',
  'a',
  'image',
  'use',
  'animate',
  'animatemotion',
  'animatetransform',
  'set',
  'style',
]);

function isExternalUrl(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  return (
    trimmed.startsWith('http:') ||
    trimmed.startsWith('https:') ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('javascript:') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  );
}

function cleanElement(el: Element): void {
  const tag = el.tagName.toLowerCase();
  if (FORBIDDEN_TAGS.has(tag)) {
    el.remove();
    return;
  }
  if (!ALLOWED_TAGS.has(tag)) {
    el.remove();
    return;
  }
  const attrs = Array.from(el.attributes);
  for (const attr of attrs) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on')) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (name === 'href' || name === 'xlink:href') {
      if (isExternalUrl(attr.value)) {
        el.removeAttribute(attr.name);
      }
      continue;
    }
    if (name === 'style') {
      const value = attr.value.toLowerCase();
      if (value.includes('url(') || value.includes('expression')) {
        el.removeAttribute(attr.name);
      }
      continue;
    }
  }
  const children = Array.from(el.children);
  for (const child of children) cleanElement(child);
}

export function sanitizeSvg(source: string): string {
  if (!source || typeof source !== 'string') return '';
  if (typeof DOMParser === 'undefined') return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(source, 'image/svg+xml');
    const parserError = doc.querySelector('parsererror');
    if (parserError) return '';
    const svg = doc.documentElement;
    if (!svg || svg.tagName.toLowerCase() !== 'svg') return '';
    cleanElement(svg);
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svg);
  } catch {
    return '';
  }
}

/**
 * sanitize 済 SVG を `data:image/svg+xml,...` URL に変換。
 * `<img src>` / `addImage` などに直接埋め込み可能。
 */
export function svgToDataUrl(svgSource: string): string {
  const sanitized = sanitizeSvg(svgSource);
  if (!sanitized) return '';
  const encoded = encodeURIComponent(sanitized).replace(/'/g, '%27').replace(/"/g, '%22');
  return `data:image/svg+xml;charset=utf-8,${encoded}`;
}

/**
 * SVG ファイル (User upload) を読み込んで sanitize 済文字列を返す。
 * 失敗時は Error throw (caller で alert/toast 表示)。
 */
export async function readSvgFile(file: File): Promise<string> {
  const text = await file.text();
  const sanitized = sanitizeSvg(text);
  if (!sanitized) {
    throw new Error('SVG ファイルの読込・サニタイズに失敗しました');
  }
  return sanitized;
}

/**
 * SVG source 中の `currentColor` を指定色に置換 (rasterize 前の tint 用)。
 * 旧 decorationRasterizer.applyCurrentColor 同等。
 */
export function applyCurrentColor(svg: string, color: string): string {
  return svg.replace(/currentColor/gi, color);
}
